import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useActionData } from 'react-router-dom'

import { StyledActionButton } from '@/components/Buttons.tsx'
import { FormStyled, FormSubmit } from '@/components/templates/Form.tsx'
import { FormField } from '@/components/templates/FormField'
import { ScrollableContent } from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Description, Header2 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { ActionData } from '@/screens/velthuis/PatientDetails/actions.ts'
import { useAppointmentStore } from '@/store.ts'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps.ts'

const PatientDetails = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'patientDetails',
  })
  const {
    patientData: {
      dateOfBirth,
      email,
      firstName,
      initials,
      lastName,
      phone,
    } = {},
  } = useAppointmentStore.getState()

  const { trackFormError, trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.PatientDetails,
  )
  const actionData = useActionData() as ActionData | undefined

  useEffect(() => {
    const error = actionData?.errors
      ? actionData.errors.map((e) => e.message).join(',')
      : actionData?.error
    if (error) {
      trackFormError({
        error,
      })
    }
  }, [actionData?.errors, actionData?.error])

  return (
    <ScreenWrapper>
      <FormStyled method="post">
        <Header2>{t('header')}</Header2>
        <Description>{t('description')}</Description>

        <ScrollableContent>
          <FormField
            fieldName="initials"
            fieldType="text"
            defaultValue={initials}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="firstName"
            fieldType="text"
            defaultValue={firstName}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="lastName"
            fieldType="text"
            defaultValue={lastName}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="dateOfBirth"
            fieldType="date"
            defaultValue={dateOfBirth}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="email"
            fieldType="email"
            defaultValue={email}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="phone"
            fieldType="tel"
            defaultValue={phone}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
        </ScrollableContent>

        <FormSubmit>
          <StyledActionButton variant="Primary" type="submit">
            {t('actions.continue')}
          </StyledActionButton>
        </FormSubmit>
      </FormStyled>
    </ScreenWrapper>
  )
}

export { PatientDetails }
